import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./DashboardHome.css";
import { getProfile } from "../../../api/auth";
import { getBlogs } from "../../../api/blogs";
import { isBlogPublished } from "../../../utils/blogStatus";
import {
    Activity,
    ArrowRight,
    BookOpen,
    Calendar,
    LayoutDashboard,
    PenSquare,
    Shield,
    Sparkles,
    UserRound,
} from "lucide-react";

export default function DashboardHome() {
    const [user, setUser] = useState(null);
    const [blogMetrics, setBlogMetrics] = useState({ total: null, published: null, drafts: null });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;

        const fetchData = async () => {
            try {
                const [profile, blogsRaw] = await Promise.all([
                    getProfile(),
                    getBlogs().catch(() => null),
                ]);
                if (cancelled) return;
                setUser(profile);
                if (Array.isArray(blogsRaw)) {
                    const published = blogsRaw.filter(isBlogPublished).length;
                    const drafts = blogsRaw.length - published;
                    setBlogMetrics({
                        total: blogsRaw.length,
                        published,
                        drafts,
                    });
                } else {
                    setBlogMetrics({ total: null, published: null, drafts: null });
                }
            } catch (err) {
                if (cancelled) return;
                const statusCode = err?.response?.status;
                if (statusCode === 401 || statusCode === 403) {
                    setError("No token found or session expired. Please login.");
                } else {
                    setError(err?.response?.data?.message || err?.message || "Failed to fetch user");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchData();
        return () => {
            cancelled = true;
        };
    }, []);

    const createdAtText = useMemo(() => {
        if (!user?.createdAt) return "—";
        try {
            return new Date(user.createdAt).toLocaleString();
        } catch {
            return String(user.createdAt);
        }
    }, [user?.createdAt]);

    const createdAtDateOnlyText = useMemo(() => {
        if (!user?.createdAt) return "—";
        try {
            const d = new Date(user.createdAt);
            if (Number.isNaN(d.getTime())) return "—";
            return d.toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
            });
        } catch {
            return "—";
        }
    }, [user?.createdAt]);

    const roleLabel = (user?.role || "User").toString();

    const userInitials = useMemo(() => {
        const source = user?.name || user?.email || "User";
        const parts = String(source).trim().split(/\s+/).filter(Boolean);
        if (parts.length === 0) return "U";
        if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
        return `${parts[0].slice(0, 1)}${parts[1].slice(0, 1)}`.toUpperCase();
    }, [user?.name, user?.email]);

    const todayLong = useMemo(() => {
        try {
            return new Date().toLocaleDateString(undefined, {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
            });
        } catch {
            return "Today";
        }
    }, []);

    const todayShort = useMemo(() => {
        try {
            return new Date().toLocaleDateString(undefined, {
                weekday: "short",
                month: "short",
                day: "numeric",
            });
        } catch {
            return "";
        }
    }, []);

    const welcomeName = user?.name?.trim() || user?.email?.split("@")[0] || "there";

    const shortId = useMemo(() => {
        const id = user?._id;
        if (!id || typeof id !== "string") return "—";
        if (id.length <= 14) return id;
        return `${id.slice(0, 8)}…${id.slice(-4)}`;
    }, [user?._id]);

    return (
        <section className="dashhome" aria-label="Dashboard home">
            <header className="dashhome-pagehead">
                <div className="dashhome-pagehead-inner">
                    <div className="dashhome-pagehead-main">
                        <p className="dashhome-eyebrow">
                            <LayoutDashboard className="dashhome-eyebrow-icon" size={15} strokeWidth={2} aria-hidden />
                            Admin workspace
                        </p>
                        <h1 className="dashhome-h1">Command Center</h1>
                        <p className="dashhome-lede">
                            Create posts, review your library, and manage your account — without leaving this panel.
                        </p>
                    </div>
                    <div className="dashhome-pagehead-meta">
                        <div className="dashhome-chip dashhome-chip--date" title={todayLong}>
                            <Calendar size={15} strokeWidth={2} aria-hidden />
                            <span>{todayShort}</span>
                        </div>
                        <div className="dashhome-chip dashhome-chip--live">
                            <span className="dashhome-live-dot" aria-hidden />
                            <span>Live</span>
                        </div>
                    </div>
                </div>
            </header>

            <section className="dashhome-welcome" aria-label="Welcome">
                <div className="dashhome-welcome-bg" aria-hidden />
                <div className="dashhome-welcome-inner">
                    <div className="dashhome-welcome-copy">
                        <p className="dashhome-welcome-kicker">
                            <Sparkles size={14} strokeWidth={2} className="dashhome-welcome-kicker-icon" aria-hidden />
                            {loading ? "Loading workspace" : "Today"}
                        </p>
                        <h2 className="dashhome-welcome-title">
                            {loading ? "Preparing your dashboard" : `Welcome back, ${welcomeName}`}
                        </h2>
                        <p className="dashhome-welcome-sub">{todayLong}</p>
                    </div>
                    <div className="dashhome-welcome-actions">
                        <Link to="/dashboard/add-blog" className="dashhome-cta dashhome-cta--primary">
                            <PenSquare size={17} strokeWidth={2} aria-hidden />
                            Create blog
                        </Link>
                        <Link to="/dashboard/blog-list" className="dashhome-cta dashhome-cta--ghost">
                            <BookOpen size={17} strokeWidth={2} aria-hidden />
                            Manage posts
                        </Link>
                    </div>
                </div>
            </section>

            {!loading && !error && (
                <div className="dashhome-shortcuts-shell">
                    <header className="dashhome-shortcuts-head">
                        <div className="dashhome-shortcuts-head-icon" aria-hidden>
                            <Sparkles size={20} strokeWidth={2} />
                        </div>
                        <div className="dashhome-shortcuts-head-copy">
                            <p className="dashhome-shortcuts-eyebrow">Navigate</p>
                            <h2 className="dashhome-shortcuts-title">Shortcuts</h2>
                        </div>
                    </header>
                    <nav className="dashhome-shortcuts" aria-label="Shortcuts">
                        <Link to="/dashboard/add-blog" className="dashhome-shortcut">
                            <span className="dashhome-shortcut-icon" aria-hidden>
                                <PenSquare size={20} strokeWidth={2} />
                            </span>
                            <span className="dashhome-shortcut-body">
                                <span className="dashhome-shortcut-title">New blog post</span>
                                <span className="dashhome-shortcut-desc">Rich text editor &amp; cover image</span>
                            </span>
                            <ArrowRight className="dashhome-shortcut-arrow" size={18} strokeWidth={2} aria-hidden />
                        </Link>
                        <Link to="/dashboard/blog-list" className="dashhome-shortcut">
                            <span className="dashhome-shortcut-icon" aria-hidden>
                                <BookOpen size={20} strokeWidth={2} />
                            </span>
                            <span className="dashhome-shortcut-body">
                                <span className="dashhome-shortcut-title">All posts</span>
                                <span className="dashhome-shortcut-desc">Edit, preview, or remove entries</span>
                            </span>
                            <ArrowRight className="dashhome-shortcut-arrow" size={18} strokeWidth={2} aria-hidden />
                        </Link>
                        <Link to="/dashboard/profile" className="dashhome-shortcut">
                            <span className="dashhome-shortcut-icon" aria-hidden>
                                <UserRound size={20} strokeWidth={2} />
                            </span>
                            <span className="dashhome-shortcut-body">
                                <span className="dashhome-shortcut-title">Profile</span>
                                <span className="dashhome-shortcut-desc">Account details &amp; security</span>
                            </span>
                            <ArrowRight className="dashhome-shortcut-arrow" size={18} strokeWidth={2} aria-hidden />
                        </Link>
                    </nav>
                </div>
            )}

            {loading && (
                <div className="dashhome-grid dashhome-grid--loading" aria-busy="true">
                    <div className="dashhome-main">
                        <div className="dashhome-stats dashhome-stats--skeleton">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="dashhome-stat dashhome-stat--skeleton">
                                    <div className="dashhome-skeleton dashhome-skeleton--label" />
                                    <div className="dashhome-skeleton dashhome-skeleton--value" />
                                    <div className="dashhome-skeleton dashhome-skeleton--sub" />
                                </div>
                            ))}
                        </div>
                        <div className="dashhome-hint dashhome-hint--skeleton">
                            <div className="dashhome-skeleton" style={{ width: "38%" }} />
                            <div className="dashhome-skeleton" style={{ width: "92%" }} />
                        </div>
                    </div>

                    <aside className="dashhome-profile dashhome-profile--skeleton">
                        <div className="dashhome-profile-head">
                            <div className="dashhome-skeleton" style={{ width: "38%", height: 14 }} />
                            <div className="dashhome-skeleton" style={{ width: 56, height: 24, borderRadius: 999 }} />
                        </div>
                        <div className="dashhome-profile-hero-skel">
                            <div className="dashhome-skeleton dashhome-skeleton--avatar" />
                            <div style={{ flex: 1 }}>
                                <div className="dashhome-skeleton" style={{ width: "72%", marginBottom: 8 }} />
                                <div className="dashhome-skeleton" style={{ width: "88%" }} />
                            </div>
                        </div>
                        <div className="dashhome-kv dashhome-kv--skeleton">
                            {[1, 2, 3, 4].map((i) => (
                                <React.Fragment key={i}>
                                    <div className="dashhome-skeleton" style={{ height: 11 }} />
                                    <div className="dashhome-skeleton" style={{ height: 13 }} />
                                </React.Fragment>
                            ))}
                        </div>
                    </aside>
                </div>
            )}

            {!loading && error && (
                <div className="dashhome-alert dashhome-alert--error" role="alert">
                    {error}
                </div>
            )}

            {!loading && !error && user && (
                <div className="dashhome-grid ">
                    <div className="dashhome-main">
                        <section className="dashhome-stats-shell" aria-label="Overview">
                            <header className="dashhome-stats-head">
                                <div className="dashhome-stats-head-icon" aria-hidden>
                                    <Activity size={20} strokeWidth={2} />
                                </div>
                                <div className="dashhome-stats-head-copy">
                                    <p className="dashhome-stats-eyebrow">Overview</p>
                                    <h2 className="dashhome-stats-heading">Workspace snapshot</h2>
                                </div>
                            </header>
                            <div className="dashhome-stats">
                                <article className="dashhome-stat">
                                    <div className="dashhome-stat-icon" aria-hidden>
                                        <Shield size={22} strokeWidth={2} />
                                    </div>
                                    <div className="dashhome-stat-body">
                                        <h3 className="dashhome-stat-label">Access role</h3>
                                        <p className="dashhome-stat-value">{roleLabel}</p>
                                        <p className="dashhome-stat-hint">Permissions in this workspace</p>
                                    </div>
                                </article>
                                <article className="dashhome-stat">
                                    <div className="dashhome-stat-icon dashhome-stat-icon--teal" aria-hidden>
                                        <Calendar size={22} strokeWidth={2} />
                                    </div>
                                    <div className="dashhome-stat-body">
                                        <h3 className="dashhome-stat-label">Member since</h3>
                                        <p className="dashhome-stat-value">{createdAtDateOnlyText}</p>
                                        <p className="dashhome-stat-hint">Profile created</p>
                                    </div>
                                </article>
                                <article className="dashhome-stat">
                                    <div className="dashhome-stat-icon dashhome-stat-icon--violet" aria-hidden>
                                        <BookOpen size={22} strokeWidth={2} />
                                    </div>
                                    <div className="dashhome-stat-body">
                                        <h3 className="dashhome-stat-label">Blog posts</h3>
                                        <p className="dashhome-stat-value">
                                            {blogMetrics.total !== null ? String(blogMetrics.total) : "—"}
                                        </p>
                                        <p className="dashhome-stat-hint">
                                            {blogMetrics.total !== null
                                                ? `${blogMetrics.published} published · ${blogMetrics.drafts} drafts`
                                                : "Library overview"}
                                        </p>
                                    </div>
                                </article>
                            </div>
                        </section>

                    </div>

                    <aside className="dashhome-profile" aria-label="Your profile">
                        <div className="dashhome-profile-accent" aria-hidden />
                        <div className="dashhome-profile-inner">
                            <div className="dashhome-profile-head">
                                <div className="dashhome-profile-head-lead">
                                    <span className="dashhome-profile-head-icon" aria-hidden>
                                        <UserRound size={20} strokeWidth={2} />
                                    </span>
                                    <div className="dashhome-profile-head-text">
                                        <p className="dashhome-profile-eyebrow">Your account</p>
                                        <h2 className="dashhome-profile-title">Profile</h2>
                                    </div>
                                </div>
                                <span className="dashhome-role-pill">{roleLabel}</span>
                            </div>

                            <div className="dashhome-profile-hero">
                                <div className="dashhome-avatar" aria-hidden>
                                    {userInitials}
                                </div>
                                <div className="dashhome-profile-hero-text">
                                    <p className="dashhome-profile-name">{user.name || "Account"}</p>
                                    <p className="dashhome-profile-email">{user.email || "—"}</p>
                                </div>
                            </div>

                            <dl className="dashhome-kv">
                                <dt>User ID</dt>
                                <dd title={user._id || ""}>
                                    <code className="dashhome-id">{shortId}</code>
                                </dd>
                                <dt>Name</dt>
                                <dd>{user.name || "—"}</dd>
                                <dt>Email</dt>
                                <dd>{user.email || "—"}</dd>
                                <dt>Created</dt>
                                <dd>{createdAtText}</dd>
                            </dl>

                            <Link to="/dashboard/profile" className="dashhome-profile-link">
                                Manage profile
                                <ArrowRight size={16} strokeWidth={2} aria-hidden />
                            </Link>

                            <p className="dashhome-footnote">
                                Session issues? Sign out and sign in again so a fresh token is stored.
                            </p>
                        </div>
                    </aside>
                </div>
            )}
        </section>
    );
}
